import {
  getFieldCandidates,
  getSectionLegacyAliases,
  getSiteContentDefault,
} from './siteContentSchema';

const isObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const isPresentContentValue = (value) => {
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return value !== undefined && value !== null;
};

const parseMaybeJsonObject = (value) => {
  if (!value || typeof value !== 'string') return null;

  try {
    const parsed = JSON.parse(value);
    return isObject(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const normalizeSiteContent = (siteContent) => {
  const rows = siteContent?.data ?? siteContent ?? [];

  if (Array.isArray(rows)) {
    return rows.reduce((acc, item) => {
      if (item?.section) {
        acc[item.section] = item;
      }
      return acc;
    }, {});
  }

  if (rows?.section) {
    return { [rows.section]: rows };
  }

  return isObject(rows) ? rows : {};
};

const readFieldValue = (sectionData, fieldKey) => {
  if (!isObject(sectionData)) return undefined;

  const directValue = sectionData[fieldKey];
  if (isPresentContentValue(directValue)) return directValue;

  const contentObject = isObject(sectionData.content)
    ? sectionData.content
    : parseMaybeJsonObject(sectionData.content);
  const nestedValue = contentObject?.[fieldKey];
  if (isPresentContentValue(nestedValue)) return nestedValue;

  if (fieldKey === 'content' && isPresentContentValue(sectionData.content)) {
    return sectionData.content;
  }

  return undefined;
};

const getValueFromSection = (contentMap, sectionKey, fieldCandidates) => {
  const sectionData = contentMap[sectionKey];

  for (const candidate of fieldCandidates) {
    const value = readFieldValue(sectionData, candidate);
    if (isPresentContentValue(value)) return value;
  }

  return undefined;
};

export const getCmsValue = (siteContent, sectionKey, fieldKey) => {
  const contentMap = normalizeSiteContent(siteContent);
  const fieldCandidates = getFieldCandidates(sectionKey, fieldKey);
  const directValue = getValueFromSection(contentMap, sectionKey, fieldCandidates);

  if (isPresentContentValue(directValue)) return directValue;

  for (const legacySection of getSectionLegacyAliases(sectionKey)) {
    const legacyValue = getValueFromSection(contentMap, legacySection, fieldCandidates);
    if (isPresentContentValue(legacyValue)) return legacyValue;
  }

  return getSiteContentDefault(sectionKey, fieldKey);
};

const isSafeCmsUrl = (value) => (
  typeof value === 'string'
  && (value.startsWith('/') || value.startsWith('#'))
);

export const getCmsUrlValue = (siteContent, sectionKey, fieldKey) => {
  const value = getCmsValue(siteContent, sectionKey, fieldKey);

  return isSafeCmsUrl(value) ? value : getSiteContentDefault(sectionKey, fieldKey);
};
